import React from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, PassOut, PassStatus } from '../../../../../api';
import { getOrderPage, getPassOrderPage } from '../../../../../utils/AccountUtils';
import { getDaysLeft } from '../../../../../passes/management/utils';

export const statusColor = (status: PassStatus): 'success' | 'warning' | 'default' | 'error' => {
  if (status === PassStatus.ACTIVE) return 'success';
  if (status === PassStatus.PENDING_PAYMENT) return 'warning';
  if (status === PassStatus.ARCHIVED) return 'error';
  return 'default';
};

type PassCardProps = {
  pass: PassOut;
  selected: boolean;
  onClick: () => void;
  account: AccountData;
  language: string;
};

export const PassCard = ({ pass, selected, onClick, account, language }: PassCardProps) => {
  const { t } = useTranslation();
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const usedPunches = pass.redeemed_order_ids?.length ?? 0;
  const totalPunches =
    pass.num_punches_override ?? (passOrderPage?.num_punches != null ? passOrderPage.num_punches : '∞');
  const daysLeft = getDaysLeft(pass, passOrderPage);
  const isExpired = daysLeft !== null && daysLeft < 0;
  const workshopNames = (passOrderPage?.redeemable_on ?? [])
    .map((item) => getOrderPage(account, item.order_page_id)?.label?.[language])
    .filter(Boolean)
    .join(', ');

  return (
    <Grid
      container
      flexDirection="column"
      gap={0.5}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'mb.control',
        borderRadius: '8px',
        p: 1.5,
        bgcolor: selected ? 'primary.light' : undefined,
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}>
      <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1}>
        <Grid item sx={{ flex: 1, minWidth: 0 }}>
          <Grid container alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography fontWeight={700}>
              {pass.firstname} {pass.lastname}
            </Typography>
            {(passOrderPage?.label as any)?.[language] && (
              <Typography variant="body2" color="text.secondary">
                · {(passOrderPage!.label as any)[language]}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              #{pass.order_number}
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
          {isExpired ? (
            <Chip label={t('passes.expired')} color="error" size="small" variant="outlined" />
          ) : (
            <Chip label={t(`pass_status.${pass.status.toLowerCase()}`)} color={statusColor(pass.status)} size="small" />
          )}
        </Box>
      </Grid>
      <Grid container alignItems="center" gap={1.5} flexWrap="wrap">
        <Typography variant="body2">{t('passes.punches_used', { used: usedPunches, total: totalPunches })}</Typography>
        {pass.phone && (
          <Typography variant="body2" color="text.secondary" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {pass.phone}
          </Typography>
        )}
        {pass.email && (
          <Typography variant="body2" color="text.secondary">
            {pass.email}
          </Typography>
        )}
        {pass.expiration_date && (
          <Typography variant="body2" color="text.secondary">
            {t('passes.expires')}: {moment(pass.expiration_date).format('DD/MM/YYYY')}
          </Typography>
        )}
      </Grid>
      {workshopNames && (
        <Typography variant="body2" color="text.secondary">
          {workshopNames}
        </Typography>
      )}
    </Grid>
  );
};
